/**
 * The save-state machine (principle 15: never show "Saved" without saying where).
 *
 * This exists as a derivation rather than a flag the UI sets, because the failure mode it guards
 * against is subtle: a component that owns its own "saved" boolean will eventually be right about
 * the write and wrong about the sync, and the user will close a tab believing work is safe.
 *
 * `deriveSaveState` is the single place that decision is made. Its central guarantee is asserted
 * in the tests: it can never report `synced` while operations are still waiting to be sent.
 */
export const SAVE_STATES = [
    'saving',
    'saved-locally',
    'syncing',
    'synced',
    'files-pending',
    'offline',
    'sync-error',
    'conflict',
    'recovery-available',
];
/**
 * Precedence, most consequential first.
 *
 * Corruption outranks everything: a user looking at a possibly-damaged document must not be told
 * it is "synced". Conflict outranks transient progress for the same reason — it needs a decision,
 * and progress indicators invite waiting instead.
 */
export function deriveSaveState(input) {
    if (input.recoveryAvailable)
        return 'recovery-available';
    if (input.hasConflict)
        return 'conflict';
    if (input.localWriteInFlight)
        return 'saving';
    if (input.pendingOperations > 0) {
        if (input.lastSyncFailed)
            return 'sync-error';
        if (!input.online)
            return 'offline';
        if (input.syncInFlight)
            return 'syncing';
        return 'saved-locally';
    }
    return input.filesPending === true ? 'files-pending' : 'synced';
}
/**
 * Is the user's work safe if the process dies right now?
 *
 * True everywhere except `saving`: once the journal write has landed, every other state is a
 * statement about the *server*, not about whether the edit survives a crash.
 */
export function isDurable(state) {
    return state !== 'saving';
}
/** States the user has to act on. The UI must not let these pass as ambient status text. */
export function requiresAttention(state) {
    return state === 'conflict' || state === 'sync-error' || state === 'recovery-available';
}
/** Is everything the user has done reflected on the server? */
export function isFullySynced(state) {
    return state === 'synced';
}
/**
 * Translation keys rather than strings: the product ships in twelve languages, and a state
 * machine that hard-codes English becomes twelve places to change.
 */
export const SAVE_STATE_LABEL_KEYS = {
    saving: 'saveState.saving',
    'saved-locally': 'saveState.savedLocally',
    syncing: 'saveState.syncing',
    synced: 'saveState.synced',
    'files-pending': 'saveState.filesPending',
    offline: 'saveState.offline',
    'sync-error': 'saveState.syncError',
    conflict: 'saveState.conflict',
    'recovery-available': 'saveState.recoveryAvailable',
};
//# sourceMappingURL=save-state.js.map